/**
 * Subscribes to real-time chat_enabled updates from admin site configuration.
 * Updates the user init query cache so all consumers see the new value immediately.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { storage } from '@/lib/storage';
import type { UserInitData } from '@/hooks/queries/useUserInitQuery';

export function useGlobalChatEnabledLive() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    let unsubscribe: (() => void) | null = null;

    import('@/lib/websocket-client').then(({ websocketClient }) => {
      websocketClient.connect(token).catch(() => {});

      unsubscribe = websocketClient.subscribe(
        'chat:enabled:update',
        (data: { enabled: boolean }) => {
          queryClient.setQueryData<UserInitData>(['user', 'init'], (prev) =>
            prev ? { ...prev, chatEnabled: data.enabled === true } : prev,
          );
        },
      );
    });

    return () => {
      unsubscribe?.();
    };
  }, [user, queryClient]);
}
